import type { SidebarCustomer } from "@/api/sidebarEntryAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown } from "@fortawesome/sharp-regular-svg-icons";
import { useNavigate } from "@tanstack/react-router";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "frontend-components";
import { useTranslation } from "react-i18next";
import UserMenu from "./UserMenu";

function CustomerSwitcher({
    selectedCustomer,
    availableCustomers,
}: {
    selectedCustomer?: SidebarCustomer;
    availableCustomers: SidebarCustomer[];
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!selectedCustomer && availableCustomers.length === 0) {
        return null;
    }

    if (selectedCustomer && availableCustomers.length <= 1) {
        return (
            <div className="border-border text-foreground truncate border-b px-4 py-4 text-[15px] leading-none font-semibold tracking-normal">
                {selectedCustomer.name}
            </div>
        );
    }

    const selectedCustomerName = selectedCustomer?.name ?? t("common.selectCustomer");

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label={t("common.chooseCustomer")}
                className="border-border text-foreground hover:bg-muted focus-visible:ring-outline flex w-full cursor-pointer items-center justify-between gap-2 border-b px-4 py-4 text-[15px] leading-none font-semibold tracking-normal outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
            >
                <span className="min-w-0 truncate">{selectedCustomerName}</span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-muted-foreground size-3 shrink-0"
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-60">
                <DropdownMenuGroup>
                    <DropdownMenuLabel>{t("common.chooseCustomer")}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {availableCustomers.map((customer) => (
                        <DropdownMenuItem
                            key={customer.id}
                            onClick={() =>
                                void navigate({
                                    to: "/$customerId",
                                    params: { customerId: String(customer.id) },
                                })
                            }
                        >
                            <span className="min-w-0 flex-1 truncate">{customer.name}</span>
                            {customer.id === selectedCustomer?.id ? (
                                <FontAwesomeIcon icon={faCheck} className="ml-auto size-3" />
                            ) : null}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function SidebarUser({
    selectedCustomer,
    availableCustomers,
}: {
    selectedCustomer?: SidebarCustomer;
    availableCustomers: SidebarCustomer[];
}) {
    return (
        <div className="border-border mt-auto border-t">
            <CustomerSwitcher
                selectedCustomer={selectedCustomer}
                availableCustomers={availableCustomers}
            />
            <div className="hidden lg:block">
                <UserMenu variant="full" />
            </div>
        </div>
    );
}
